import { React, useState, useEffect, useRef } from "react";
import Header from "../../../Common/header";
import Footer from "../../../Common/footer";
import { useLocation } from "react-router-dom";
import { useHistory } from "react-router-dom";
import { API } from "../../../Common/lib/api";
import { Storage } from "../../../Common/lib/storage";
import { getUserSession } from "../../../Common/lib/Session";
import { isProjectOwnedByUser } from "../../../Common/lib/projectOwnership";
import KeywordInput from "./keywordInput";
import SimpleReactValidator from "simple-react-validator";
import { MapContainer, TileLayer } from "react-leaflet";
import AreaSelect from "../../DataVisualization/GeneralMaps/AreaSelect";
import ConfirmSaveModal from "../Modals/confirmSaveModal";
import useDocumentTitle from "../../../Common/useDocumentTitle";

export default function EditProject() {
  const [initialProject, setInitialProject] = useState();
  const [project, setProject] = useState();
  const [keywords, setKeywords] = useState([]);
  const [vessels, setVessels] = useState([]);
  const [stakeholders, setStakeholders] = useState([]);
  const [attachmentKeys, setAttachmentKeys] = useState([]);
  const [updateBounds, setUpdateBounds] = useState(false);
  const [showCoordinates, setShowCoordinates] = useState(true);
  let history = useHistory();
  let { search } = useLocation();
  const query = new URLSearchParams(search);
  const projectId = query.get("projectID");
  const simpleValidator = useRef(new SimpleReactValidator());
  const [, forceUpdate] = useState();
  const [selectArea, setSelectArea] = useState(false);
  const [openConfirmModal, setOpenConfirmModal] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [selectedAttachments, setSelectedAttachments] = useState([]);

  useDocumentTitle("Edit Project");

  const [modalAction, setModalAction] = useState('');
  useEffect(() => {
    const fetchData = async () => {
      try {
        const projectQuery = await API.projects.getById(projectId);
        const project = projectQuery.data.getProject;
        
        if (!project) {
          history.push("/");
          return;
        }
        
        // Check if user is the publisher of this project or is staff
        const user = await getUserSession();
        const isPublisher = isProjectOwnedByUser(project, user);
        const isStaff = user.is_staff === true || user.is_staff === "true";
        
        if (!isPublisher && !isStaff) {
          history.push("/");
          return;
        }

        //if the project is locked and user is not staff, return to the profile page.
        if (project.locked && !isStaff) {
          history.push("/profile?tab=My+Contributions");
          return;
        }

        //if the user is locked from editing any projects and is not staff, return to the profile page.
        if (user?.edit_locked === "true" && !isStaff){
          history.push("/profile?tab=My+Contributions");
          return;
        }

        // Update the attachments state with the fetched data
        if (project.attachmentKeys) {
          setAttachments(JSON.parse(project.attachmentKeys));
        }

        setProject(project);
        setInitialProject(project);
        setKeywords(JSON.parse(project.keywords || "[]"));
        setVessels(JSON.parse(project.vessels || "[]"));
        setStakeholders(JSON.parse(project.stakeholders || "[]"));
      } catch (error) {
        console.error('Error fetching project:', error);
        history.push("/");
      }
    };

    fetchData();
  }, []);

  const handleAttachmentSelect = (key, isSelected) => {
    setSelectedAttachments(prev => {
      const updatedSelection = isSelected 
        ? [...prev, key] 
        : prev.filter(item => item !== key);
  
      // Update the attachmentKeys value to the remaining attachments
      const updatedAttachmentKeys = attachments.filter(
        attachment => !updatedSelection.includes(attachment.key)
      );
      setAttachmentKeys(updatedAttachmentKeys);
      
      return updatedSelection;
    });
  };
  
  

  // Used to create a 19 digit ID to append to PK's.
  function flakeID() {
    const flakeID = new FlakeID();
    return intformat(flakeID.next(), "dec");
  }

  /**
   * Will determine if any changes were made to the project. If they were, an update Administration item will be created
   * and the edits made to the project will be saved.
   * @param {*} event
   */
  const handleSubmit = async () => {
    // Check if user is the publisher of the project before updating project
    const user = await getUserSession();
    if ("publisher-" + user.username) {
      try {
        if (simpleValidator.current.allValid()) {
          const updatedFields = getUpdatedFields();
          
        // Remove selected attachments from S3
        for (const key of selectedAttachments) {
          try {
            await Storage.remove(key, { level: 'public' });
          } catch (error) {
            //console.error('Error deleting attachment:', error);
          }
        }

          //check if anything was even changed before submiting.
          if (updatedFields.length) {
            await API.projects.update({
              id: projectId,
              userID: user.username,
              abstract: project.abstract,
              citation: project.citation,
              eastLongitude: parseFloat(project.eastLongitude),
              keywords: JSON.stringify(keywords),
              vessels: JSON.stringify(vessels),
              northLatitude: parseFloat(project.northLatitude),
              researchProgramName: project.researchProgramName,
              southLatitude: parseFloat(project.southLatitude),
              stakeholders: JSON.stringify(stakeholders),
              title: project.title,
              website: project.website,
              westLongitude: parseFloat(project.westLongitude),
              centerPoint: project.centerPoint,
              publishStatus: project.publishStatus,
              attachmentKeys: JSON.stringify(attachmentKeys),
            });
          }

          setTimeout(() => history.push("/profile?tab=My+Contributions"), 1000);
        } else {
          simpleValidator.current.showMessages();
          window.scrollTo(0, 0);
          // rerender to show messages for the first time
          forceUpdate(1);
        }
      } catch (e) {
        //console.log(e);
      }
    } else {
      // console.log("Error: Project was not saved to the database.");
    }
  };
  /**
 * DisplayAttachments is a function to render a list of attachments with options to delete.
 * Each attachment is contained in a card with a checkbox to select for deletion.
 *
 * @param {Object[]} attachmentKeys - An array of attachment objects to display.
 * @param {function} onAttachmentSelect - A function to handle the selection state of attachments.
 * @returns A JSX element representing the UI for displaying attachment cards.
 */
  const DisplayAttachments = ({ attachmentKeys, onAttachmentSelect }) => {
    const extractFilename = (path) => {
      return path.split('/').pop();
    };
    
    const handleCheckboxChange = (key, isChecked) => {
      onAttachmentSelect(key, isChecked);
    };

    if(attachmentKeys?.length > 0){
      return (
        <div>
          <label className="mt-4 block text-base font-semibold text-gray-900 dark:text-white">Files</label>
          {attachmentKeys.map((attachment, index) => (
            <div key={index} className="flex items-center bg-white justify-start shadow overflow-hidden rounded-lg p-4 my-2 dark:bg-gray-800 dark:border dark:border-gray-700">
              <div className="flex-grow">
                <div className="font-medium dark:text-white">{extractFilename(attachment.key)}</div>
                <div className="text-sm text-gray-500 dark:text-gray-400">{attachment.description}</div>
              </div>
              <div className="flex items-center">
                <label htmlFor={`checkbox-${index}`} className="mr-2 text-sm font-medium text-gray-700 dark:text-gray-400">Delete</label>
                <input
                  type="checkbox"
                  id={`checkbox-${index}`}
                  checked={selectedAttachments.includes(attachment.key)}
                  onChange={(e) => handleCheckboxChange(attachment.key, e.target.checked)}
                  className="h-5 w-5 text-blue-600 rounded border dark:bg-gray-700 dark:border-gray-600"
                />
              </div>
            </div>
          ))}
        </div>
      );
    }
  };

  /**
   * Will compare all editable fields current and original states to determine which ones have been changed.
   *
   * @returns The fields which have been updated.
   */
  function getUpdatedFields() {
    let updatedFields = [];

    //basic string fields to compare for changes.
    const fieldsToCompare = [
      "title",
      "abstract",
      "researchProgramName",
      "citation",
      "northLatitude",
      "southLatitude",
      "eastLongitude",
      "westLongitude",
      "website",
      "publishStatus",
      "attachmentKeys",
    ];

    fieldsToCompare.forEach((field) => {
      if (project[field] !== initialProject[field]) {
        updatedFields.push(field);
      }
    });

    //array fields to compare for changes
    const jsonFieldsToCompare = [
      ["keywords", keywords],
      ["vessels", vessels],
      ["stakeholders", stakeholders],
      ["attachmentKeys", attachmentKeys],
    ];

    jsonFieldsToCompare.forEach((field) => {
      if (initialProject[field[0]] !== JSON.stringify(field[1])) {
        updatedFields.push(field[0]);
      }
    });

    return updatedFields;
  }

  /**
   * Handles input field changes.
   * @param {*} field Name of userInfo property to change
   * @param {*} value Value from input field
   */
  function handleChange(field, value) {
    setProject((prevState) => ({
      ...prevState,
      [field]: value.trim(),
    }));
  }

  function onClickResetDirection(e) {
    e.preventDefault();
    setProject((prevState) => ({
      ...prevState,
      northLatitude: "",
      southLatitude: "",
      eastLongitude: "",
      westLongitude: "",
      showCoordinates: false,
    }));
    setShowCoordinates(false);
    setSelectArea(false);
  }

  function calculateCenter(north, south, east, west){
    if (east < 0) east += 360;
    if (west < 0) west += 360;

    // Calculate the average of the two longitudes
    let centerLongitude = (east + west) / 2;

    // Ensure the result is within the standard range (-180 to +180 degrees)
    if (centerLongitude < -180) centerLongitude += 360;
    if (centerLongitude > 180) centerLongitude -= 360;

    let latitude = (north + south) / 2;

    return([latitude, centerLongitude]);
  }

  function handleCoordinateChange(bound) {
    try {
      const north = bound.north;
      const east = bound.east;
      const south = bound.south;
      const west = bound.west;

    //Get the center point of the bounds.
    let centerPoint = calculateCenter(parseFloat(north), parseFloat(south), parseFloat(east), parseFloat(west))

      setProject((prevState) => ({
        ...prevState,
        northLatitude: north,
        southLatitude: south,
        eastLongitude: east,
        westLongitude: west,
        centerPoint: centerPoint,
      }));
    } catch (err) {
      // console.log(err);
    }
  }

  function handleResubmitClick() {
    setProject((prevState) => ({
      ...prevState,
      publishStatus: "under review",
    }));
    //handleSubmit();
    setOpenConfirmModal(true);
    setModalAction('resubmit'); 
  }
  

  function handleCoordinateChange(bounds) {
    setProject((prevState) => ({
      ...prevState,
      northLatitude: bounds.north,
      southLatitude: bounds.south,
      eastLongitude: bounds.east,
      westLongitude: bounds.west,
    }));
  }

  return (
    <>
      <Header />
      {project ? (
        <>
        <div className="pb-24 pt-12 bg-white dark:bg-gray-900">
          <div className="relative max-w-2xl msm:max-w-lg overflow-hidden m-auto shadow-lg border bg-gray-100 rounded-xl dark:bg-gray-800 dark:border-gray-700">
            <div className="max-w-7xl mx-auto">
              <h1 className="text-center font-extrabold text-gray-900 sm:text-xl sm:tracking-tight lg:text-2xl py-6 border-b dark:border-gray-700 dark:text-white">
                Edit Project
              </h1>
              <section className="pt-4">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                  <form className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <label className="block text-base font-medium text-gray-900 dark:text-white">
                      Project Title
                      <span className="text-red-700 font-bold border-red-200">*</span>
                    </label>
                    <input
                      type="text"
                      defaultValue={project.title}
                      className="mt-1 py-3 px-4 block w-full shadow-sm outline-none focus:ring-1 border focus:ring-blue-500 focus:border-blue-500 border-gray-300 rounded-md dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                      onChange={(e) => handleChange("title", e.target.value)}
                    />
                    {simpleValidator.current.message("Project Title", project.title, "required|string", {
                      className: "text-red-600",
                    })}

                    <label className="mt-4 block text-base font-medium text-gray-900 dark:text-white">
                      Abstract
                      <span className="text-red-700 font-bold border-red-200">*</span>
                    </label>
                    <textarea
                      rows={3}
                      cols={100}
                      className="mt-1 py-3 px-4 block w-full shadow-sm outline-none focus:ring-1 focus:ring-blue-500 border focus:border-blue-500 border-gray-300 rounded-md dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                      type="text"
                      defaultValue={project.abstract}
                      onChange={(e) => handleChange("abstract", e.target.value)}
                    />
                    {simpleValidator.current.message("Abstract", project.abstract, "required|string", {
                      className: "text-red-600",
                    })}

                    {stakeholders.map((contributor, index) => (
                      <div key={index}>
                        <label className="mt-4 block text-base font-medium text-gray-900 dark:text-white">
                          {contributor.role}
                          <span className="text-red-700 font-bold border-red-200">*</span>
                        </label>
                        <input
                          type="text"
                          value={contributor.name}
                          className="mt-1 py-3 px-4 block w-full shadow-sm outline-none focus:ring-1 border focus:ring-blue-500 focus:border-blue-500 border-gray-300 rounded-md dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                          onChange={(e) => {
                            const updatedStakeholders = [...stakeholders];
                            updatedStakeholders[index].name = e.target.value;
                            setStakeholders(updatedStakeholders);
                          }}
                        />
                        {simpleValidator.current.message(
                          contributor.role,
                          stakeholders[index].name,
                          "required|string",
                          {
                            className: "text-red-600",
                          }
                        )}
                      </div>
                    ))}

                    <label className="mt-4 block text-base font-medium text-gray-900 dark:text-white">
                      Research Program Name
                    </label>
                    <input
                      type="text"
                      defaultValue={project.researchProgramName}
                      className="mt-1 py-3 px-4 block w-full shadow-sm outline-none focus:ring-1 border focus:ring-blue-500 focus:border-blue-500 border-gray-300 rounded-md dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                      onChange={(e) => handleChange("researchProgramName", e.target.value)}
                    />

                    <label className="mt-4 block text-base font-medium text-gray-900 dark:text-white">Website</label>
                    <input
                      type="text"
                      defaultValue={project.website}
                      className="mt-1 py-3 px-4 block w-full shadow-sm outline-none focus:ring-1 border focus:ring-blue-500 focus:border-blue-500 border-gray-300 rounded-md dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                      onChange={(e) => handleChange("website", e.target.value)}
                    />

                    <KeywordInput keywords={keywords} setKeywords={setKeywords} type="Keyword" />
                    {simpleValidator.current.message("Project Keywords", keywords, "required|array", {
                      className: "text-red-600",
                    })}

                    {/* Project Area/Region section ------------------------------------------------------------- */}
                    <div className="sm:col-span-6">
                      <label className="mt-4 block text-base font-medium text-gray-900 dark:text-white">
                        Project Area/Region
                        <span className="text-red-700 font-bold border-red-200">*</span>
                      </label>
                      <div className="mt-1">
                        <div className="relative">
                          <MapContainer
                            center={[69.116864, -105.059681]}
                            minZoom={2}
                            zoom={3}
                            style={{ height: "300px", zIndex: 0 }}
                            worldCopyJump={true}
                            maxBoundsViscosity={1}
                            attributionControl={false}
                          >
                            <AreaSelect
                              selectArea={selectArea}
                              setSelectArea={setSelectArea}
                              showCoordinates={showCoordinates}
                              setShowCoordinates={setShowCoordinates}
                              setUpdateBounds={setUpdateBounds}
                              updateBounds={updateBounds}
                              handleUpdateInputFields={handleCoordinateChange}
                              bounds={[
                                [project.northLatitude, project.eastLongitude],
                                [project.southLatitude, project.westLongitude],
                              ]}
                            />
                          </MapContainer>
                          <div className="absolute right-2 top-2">
                            <button
                              onClick={() =>
                                setSelectArea(prevState => {
                                  return !prevState;
                                })
                              }
                              type="button"
                              className="w-full justify-center content-center items-center px-3 py-2 center border border-gray-400 shadow-md text-sm leading-4 font-medium rounded-md text-black bg-white hover:bg-gray-100 focus:outline-none"
                            >
                              Select Area
                            </button>
                          </div>
                        </div>
                        <div className="mt-2 text-right">
                          <button
                            onClick={(e) => onClickResetDirection(e)}
                            type="button"
                            className="w-full justify-center content-center items-center px-3 py-2 center border border-transparent text-sm leading-4 font-medium rounded-md shadow-sm text-gray-700 bg-gray-300 hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 dark:border dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600 dark:hover:text-white dark:hover:bg-gray-600"
                          >
                            Clear
                          </button>
                        </div>
                      </div>

                      {/* LAT & LONG FIELDS ------------------------------------------------*/}
                      <div className="grid grid-cols-2 gap-4 w-full py-3 mt-2">
                        <div className="flex items-end justify-start">
                          <p className="bottom-0 mb-0 font-medium text-sm dark:text-white">Latitude</p>
                        </div>
                        <div className="flex items-end justify-start">
                          <p className="bottom-0 mb-0 font-medium text-sm dark:text-white">Longitude</p>
                        </div>

                        {/* NORTH FIELD ------------------------------------------------ */}
                        <div>
                          {project.northLatitude === "" ? (
                            <input
                              className="w-full p-2 rounded-md border border-gray-300 bg-white shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:placeholder:text-gray-400"
                              type="text"
                              placeholder={"North"}
                              value={""}
                              size={10}
                              onChange={(e) => {
                                const bounds = {
                                  north: e.target.value,
                                  south: project.southLatitude,
                                  east: project.eastLongitude,
                                  west: project.westLongitude,
                                };
                                handleCoordinateChange(bounds);
                              }}
                              onBlur={() => {
                                setUpdateBounds(true);
                              }}
                            />
                          ) : (
                            <input
                              className="w-full p-2 rounded-md border border-gray-300 bg-white shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:placeholder:text-gray-400"
                              type="text"
                              placeholder={project.northLatitude}
                              value={project.northLatitude}
                              size={10}
                              onChange={(e) => {
                                const bounds = {
                                  north: e.target.value,
                                  south: project.southLatitude,
                                  east: project.eastLongitude,
                                  west: project.westLongitude,
                                };
                                handleCoordinateChange(bounds);
                              }}
                              onBlur={() => {
                                setUpdateBounds(true);
                              }}
                            />
                          )}

                          {simpleValidator.current.message(
                            "North Latitude",
                            project.northLatitude,
                            "required",
                            {
                              className: "text-red-600",
                            }
                          )}
                        </div>

                        {/* EAST FIELD ------------------------------------------------ */}
                        <div>
                          {project.eastLongitude === "" ? (
                            <input
                              className="w-full p-2 rounded-md border border-gray-300 bg-white shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:placeholder:text-gray-400"
                              type="text"
                              placeholder={"East"}
                              value={""}
                              size={10}
                              onChange={(e) => {
                                const bounds = {
                                  north: project.northLatitude,
                                  south: project.southLatitude,
                                  east: e.target.value,
                                  west: project.westLongitude,
                                };
                                handleCoordinateChange(bounds);
                              }}
                              onBlur={() => {
                                setUpdateBounds(true);
                              }}
                            />
                          ) : (
                            <input
                              className="w-full p-2 rounded-md border border-gray-300 bg-white shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:placeholder:text-gray-400"
                              type="text"
                              placeholder={project.eastLongitude}
                              value={project.eastLongitude}
                              size={10}
                              onChange={(e) => {
                                const bounds = {
                                  north: project.northLatitude,
                                  south: project.southLatitude,
                                  east: e.target.value,
                                  west: project.westLongitude,
                                };
                                handleCoordinateChange(bounds);
                              }}
                              onBlur={() => {
                                setUpdateBounds(true);
                              }}
                            />
                          )}

                          {simpleValidator.current.message(
                            "East Longitude",
                            project.eastLongitude,
                            "required",
                            {
                              className: "text-red-600",
                            }
                          )}
                        </div>

                        {/* SOUTH FIELD ------------------------------------------------ */}
                        <div>
                          {project.southLatitude === "" ? (
                            <input
                              className="w-full p-2 rounded-md border border-gray-300 bg-white shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:placeholder:text-gray-400"
                              type="text"
                              placeholder={"South"}
                              value={""}
                              size={10}
                              onChange={(e) => {
                                const bounds = {
                                  north: project.northLatitude,
                                  south: e.target.value,
                                  east: project.eastLongitude,
                                  west: project.westLongitude,
                                };
                                handleCoordinateChange(bounds);
                              }}
                              onBlur={() => {
                                setUpdateBounds(true);
                              }}
                            />
                          ) : (
                            <input
                              className="w-full p-2 rounded-md border border-gray-300 bg-white shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:placeholder:text-gray-400"
                              type="text"
                              placeholder={project.southLatitude}
                              value={project.southLatitude}
                              size={10}
                              onChange={(e) => {
                                const bounds = {
                                  north: project.northLatitude,
                                  south: e.target.value,
                                  east: project.eastLongitude,
                                  west: project.westLongitude,
                                };
                                handleCoordinateChange(bounds);
                              }}
                              onBlur={() => {
                                setUpdateBounds(true);
                              }}
                            />
                          )}

                          {simpleValidator.current.message(
                            "South Latitude",
                            project.southLatitude,
                            "required",
                            {
                              className: "text-red-600",
                            }
                          )}
                        </div>

                        {/* WEST FIELD ------------------------------------------------ */}
                        <div>
                          {project.westLongitude === "" ? (
                            <input
                              className="w-full p-2 rounded-md border border-gray-300 bg-white shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:placeholder:text-gray-400"
                              type="text"
                              placeholder={"West"}
                              value={""}
                              size={10}
                              onChange={(e) => {
                                const bounds = {
                                  north: project.northLatitude,
                                  south: project.southLatitude,
                                  east: project.eastLongitude,
                                  west: e.target.value,
                                };
                                handleCoordinateChange(bounds);
                              }}
                              onBlur={() => {
                                setUpdateBounds(true);
                              }}
                            />
                          ) : (
                            <input
                              className="w-full p-2 rounded-md border border-gray-300 bg-white shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:placeholder:text-gray-400"
                              type="text"
                              placeholder={project.westLongitude}
                              value={project.westLongitude}
                              size={10}
                              onChange={(e) => {
                                const bounds = {
                                  north: project.northLatitude,
                                  south: project.southLatitude,
                                  east: project.eastLongitude,
                                  west: e.target.value,
                                };;
                                handleCoordinateChange(bounds);
                              }}
                              onBlur={() => {
                                setUpdateBounds(true);
                              }}
                            />
                          )}

                          {simpleValidator.current.message(
                            "West Longitude",
                            project.westLongitude,
                            "required",
                            {
                              className: "text-red-600",
                            }
                          )}
                        </div>
                      </div>
                    </div>

                    <label className="mt-4 block text-base font-medium text-gray-900 dark:text-white">Citation</label>
                    <input
                      type="text"
                      defaultValue={project.citation}
                      className="mt-1 py-3 px-4 block w-full shadow-sm outline-none focus:ring-1 border focus:ring-blue-500 focus:border-blue-500 border-gray-300 rounded-md dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                      onChange={(e) => handleChange("citation", e.target.value)}
                    />

                    <KeywordInput keywords={vessels} setKeywords={setVessels} type="Vessel" />
                    {simpleValidator.current.message(
                      "Vessels/Labs/Infrastructure",
                      vessels,
                      "required|array",
                      {
                        className: "text-red-600",
                      }
                    )}

                    <DisplayAttachments attachmentKeys={JSON.parse(project.attachmentKeys)} onAttachmentSelect={handleAttachmentSelect}/>
                    <button
                      className="w-full text-center mb-0 font-medium text-blue-700 px-4 py-2 hover:underline dark:text-blue-400"
                      data-cy="add-file"
                      onClick={() => history.push("/AddFile" + "?projectID=" + project.id)}
                    >
                      Add Files
                      <span aria-hidden="true"> &rarr;</span>
                    </button>

                    <div className="flex items-center justify-center my-10">
                      <input
                        onClick={() => {
                          setOpenConfirmModal(true);
                          setModalAction(initialProject?.publishStatus === 'rejected' ? 'saveForNow' : 'submit');
                        }}
                        data-cy="submit"
                        type="button"
                        value={initialProject?.publishStatus === 'rejected' ? "Save and Exit" : "Submit Changes"}
                        className="bg-blue-600 py-3 px-5 hover:bg-blue-500 cursor-pointer text-white sm:text-base text-sm font-medium rounded-md"
                      />

                      {initialProject?.publishStatus === 'rejected' && (
                        <input
                          onClick={handleResubmitClick}
                          data-cy="resubmit"
                          type="button"
                          value="Save and Resubmit"
                          className="bg-blue-600 py-3 px-5 hover:bg-blue-500 cursor-pointer text-white sm:text-base text-sm font-medium rounded-md ml-4" 
                        />
                      )}
                    </div>
                  </form>
                </div>
              </section>
            </div>
          </div>
        </div>
        <ConfirmSaveModal setOpenConfirmModal={setOpenConfirmModal} openConfirmModal={openConfirmModal} handleSubmit={handleSubmit} action={modalAction} />
        </>
      ) : (
        <p></p>
      )}
      <Footer />
    </>
  );
}